import { sendUserAttributeVerificationCode, confirmUserAttribute } from "@aws-amplify/auth";

/**
 * Sends the verification code to the user's email and handles status updates.
 * * @param {Function} setLoader - Global state setter for the loader.
 * @param {Function} setResendDisabled - State setter for the resend button's disabled state.
 * @param {Function} setResendTimer - State setter for the resend timer countdown.
 * @param {Function} snackbar - Mobiscroll function to display toast/snackbar messages.
 */
export const sendVerificationCodeService = async ({
    setLoader,
    setResendDisabled,
    setResendTimer,
    snackbar
}) => {
    setLoader(true);
    setResendDisabled(true);
    setResendTimer(60);
    try {
        await sendUserAttributeVerificationCode({ userAttributeKey: 'email' });
        snackbar({ message: "Verification code has been sent.", color: "success" });
    } catch (error) {
        console.error("Error sending verification code:", error);
        snackbar({ message: error.message || "Failed to send code. Please try again.", color: "danger" });
        setResendDisabled(false);
        setResendTimer(0);
    } finally {
        setLoader(false);
    }
};

/**
 * Verifies the OTP, finalizes the email verification, and proceeds to login.
 * * @param {string} otp - The 6-digit code entered by the user.
 * @param {Function} setLoader - Global state setter for the loader.
 * @param {Function} setOtpErrorMessage - State setter for the OTP input error message.
 * @param {Function} setNeedsEmailVerification - State setter to hide the OTP UI.
 * @param {Function} onLogin - The function that performs final data fetching and redirection.
 * @param {Function} snackbar - Mobiscroll function to display toast/snackbar messages.
 */
export const verifyOtpAndFinalizeService = async ({
    otp,
    setLoader,
    setOtpErrorMessage,
    setNeedsEmailVerification,
    onLogin,
    snackbar
}) => {

    setLoader(true);
    snackbar({ message: "Please wait, verifying email..", color: "success" });

    try {
        await confirmUserAttribute({
            userAttributeKey: 'email',
            confirmationCode: otp
        });

        // Final check/update successful, now proceed to application login
        snackbar({ message: "Email verified successfully!", color: "success" });

        await onLogin();

    } catch (error) {
        console.error("Error verifying code:", error);
        setOtpErrorMessage(error.message || "Invalid verification code. Please try again.");
        snackbar({ message: error.message || "Invalid code.", color: "danger" });
        setNeedsEmailVerification(true)
        setLoader(false); // Turn off loader on failure
    }
};
